import { useEffect, useMemo, type ReactNode } from 'react'
import { useRouter } from 'next/router'

import { Spinner } from '@wartek-id/spinner'
import { useToast } from '@wartek-id/toast'

import { PENGELOLAAN_KINERJA_BASE_PATH } from 'app/KinerjaShared/constants/path'
import { useAPIPelaksanaanProgress } from 'api/kinerja/useAPIKinerja'
import { PRAKTIK_KINERJA_STEP_ORDER } from 'app/KinerjaPamongPage/constants/praktik-kinerja-steps'

import type { MultiPeriodPayload } from 'types/kinerja'
import type { TStatePelaksanaan } from 'types/kinerja-pelaksanaan'

type PageGuardPamongPelaksanaanStatusProps = {
  targetStep?: TStatePelaksanaan
  children?: ReactNode
  ptkId?: string
  periodParams: MultiPeriodPayload
  unstartedRedirectRoute?: string
  incorrectOrderRedirectRoute?: string
}

export function PageGuardPamongPelaksanaanStatus({
  targetStep,
  ptkId,
  periodParams,
  children,
  unstartedRedirectRoute = PENGELOLAAN_KINERJA_BASE_PATH,
  incorrectOrderRedirectRoute = PENGELOLAAN_KINERJA_BASE_PATH,
}: PageGuardPamongPelaksanaanStatusProps) {
  const toast = useToast()
  const router = useRouter()

  // -----------------------------------------
  // Get pelaksanaan progress data
  // -----------------------------------------
  const { isLoading, data, isSuccess, isFetched } = useAPIPelaksanaanProgress({
    tipe: 'SPNF',
    ptkId,
    periodParams: {
      skp_period: periodParams.skpPeriod,
      skp_year: periodParams.skpYear,
    },
    opts: {
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      refetchOnMount: false, // Prevent double fetch on re-render after invalidation
      refetchOnWindowFocus: false, // Prevent double fetch on window focus
      onError: () => {
        // Redirect to unstartedRedirectRoute if something went wrong in fetching pelaksanaan progress data
        router.replace(unstartedRedirectRoute)
      },
    },
  })

  const isCorrectFormOrder = useMemo(() => {
    // Baypass checking if the target step is not provided
    if (!targetStep) return true

    // Check if the current step (from progress data) is after (or equal to) the target step
    const currentProgressStep = data?.data?.state
    const currentProgressStepOrder =
      PRAKTIK_KINERJA_STEP_ORDER.indexOf(currentProgressStep)
    const targetStepOrder = PRAKTIK_KINERJA_STEP_ORDER.indexOf(targetStep)

    return currentProgressStepOrder >= targetStepOrder
  }, [data, targetStep])

  // -----------------------------------------
  // Redirect to Pamong pegawai homepage if pelaksanaan is not started
  // -----------------------------------------
  useEffect(() => {
    // check isFetched: wait until the Pelaksanaan query has settled (success or
    // error) before acting — avoids redirecting during the initial loading state
    if (isFetched) {
      // check !isSuccess: Pelaksanaan API returns 404 when not yet started
      // (isSuccess=false) and 200 when started (isSuccess=true)
      if (!isSuccess) {
        // If pelaksanaan is not started, redirect to errorRedirectRoute
        router.replace(unstartedRedirectRoute)
        toast({
          message: 'Pelaksanaan belum dimulai',
        })
      } else if (!isCorrectFormOrder) {
        // If pelaksanaan is started but form order is incorrect, redirect to the Praktik Kinerja hub
        router.replace(incorrectOrderRedirectRoute)

        toast({
          message: 'Lakukan sesuai urutan, ya.',
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, isSuccess])

  // -----------------------------------------
  // Render loading state
  // -----------------------------------------
  const shouldRedirect = !isSuccess || !isCorrectFormOrder

  if (isLoading || shouldRedirect) {
    return (
      <div className="flex justify-center min-h-full pt-20 bg-gray-10">
        <Spinner />
      </div>
    )
  }

  return <>{children}</>
}
